import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
  type UseFormClearErrors,
  type UseFormSetError,
} from 'react-hook-form';
import { FieldError } from '@/features/auth/components/FieldError';
import {
  formatMobileDisplay,
  parseMobileInput,
  type MobileConfig,
} from '@/features/auth/utils/mobileNumber';

type SignupMobileFieldProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  mobileConfig: MobileConfig;
  errorMessage?: string;
  setError: UseFormSetError<T>;
  clearErrors: UseFormClearErrors<T>;
};

export function SignupMobileField<T extends FieldValues>({
  name,
  control,
  mobileConfig,
  errorMessage,
  setError,
  clearErrors,
}: SignupMobileFieldProps<T>) {
  const phoneLength = mobileConfig.length;

  return (
    <div className="min-w-0">
      <label className="mb-2 block text-[13px] font-bold leading-none text-[#2E2A54]">
        Mobile <span className="text-red-500">*</span>
      </label>
      <Controller
        name={name}
        control={control}
        rules={{
          required: 'Required',
          minLength: {
            value: phoneLength,
            message: mobileConfig.message,
          },
          maxLength: {
            value: phoneLength,
            message: mobileConfig.message,
          },
          pattern: {
            value: mobileConfig.regex,
            message: mobileConfig.message,
          },
        }}
        render={({ field }) => (
          <div
            className={`signup-mobile box-border flex h-[52px] w-full min-w-0 overflow-hidden rounded-[16px] border transition-[border-color,box-shadow] ${
              errorMessage ? 'border-red-400' : 'border-[#EAE8F5]'
            }`}
          >
            <span
              className="flex h-full shrink-0 items-center whitespace-nowrap border-r border-[#EAE8F5] bg-[#F3F2F9] px-2.5 text-[12px] font-semibold text-[#3A3660]"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {mobileConfig.label}
            </span>
            <input
              inputMode="numeric"
              maxLength={mobileConfig.placeholder.length}
              placeholder={mobileConfig.placeholder}
              className="h-full min-w-0 flex-1 border-none bg-white px-3 text-sm text-[#3A3660] outline-none placeholder:text-[#A9B4B1]"
              value={formatMobileDisplay((field.value as string) || '', mobileConfig)}
              onChange={(e) => {
                const { value, rejected } = parseMobileInput(
                  e.target.value,
                  mobileConfig,
                );
                field.onChange(value);
                if (rejected) {
                  setError(name, {
                    type: 'pattern',
                    message: mobileConfig.message,
                  });
                } else {
                  clearErrors(name);
                }
              }}
              onBlur={() => {
                field.onBlur();
                const value = (field.value as string) || '';
                if (value && !mobileConfig.regex.test(value)) {
                  setError(name, {
                    type: 'pattern',
                    message: mobileConfig.message,
                  });
                }
              }}
            />
          </div>
        )}
      />
      <FieldError message={errorMessage} />
    </div>
  );
}
